import {
  CreateReminderDtoCustomRecurrenceUnitEnum,
  CreateReminderDtoCustomRecurrenceWeekOfMonthEnum,
  CreateReminderDtoNotificationChannelsEnum,
  CreateReminderDtoRecurrenceTypeEnum,
  GetMedicineDto,
  GetProfileMedicationsDto,
  GetProfileMedicationsDtoLinkedReminder,
} from 'woofwoof-api';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  MedicationStockFormValues,
  buildStockDefaults,
} from './medicationStockValues';

/** What the treatment editor seeds the form with when a row is reopened. */
export type MedicationFormInitialValues = Partial<
  Pick<
    MedicationsFormModalValuesProps,
    'dosage' | 'dosageInterval' | 'notes' | 'dosagePending'
  >
>;

interface MedicationFormDefaultsArgs {
  medication?: GetProfileMedicationsDto;
  linkedReminder?: GetProfileMedicationsDtoLinkedReminder | null;
  initialMedicine?: GetMedicineDto | null;
  initialMedicineName?: string;
  initialValues?: MedicationFormInitialValues;
  hideReminder?: boolean;
  /** Overrides the stock values read from `medication` (treatment rows). */
  initialStock?: MedicationStockFormValues;
}

export function buildMedicationFormDefaults({
  medication,
  linkedReminder,
  initialMedicine,
  initialMedicineName,
  initialValues,
  hideReminder = false,
  initialStock,
}: MedicationFormDefaultsArgs): MedicationsFormModalValuesProps {
  const seed = !medication ? initialMedicine ?? null : null;

  return {
    medicineProductId: medication?.medicineProductId ?? seed?.productId,
    name: medication?.name || seed?.productName || initialMedicineName || '',
    preparationType: medication?.preparationType ?? seed?.preparationType,

    strengthValue: medication?.strengthValue ?? '',
    strengthUnit: medication?.strengthUnit ?? '',
    strengthUnitCustom: medication?.strengthUnitCustom ?? '',
    form: medication?.form ?? '',
    formCustom: medication?.formCustom ?? '',
    quantityValue:
      medication?.quantityValue ?? medication?.dosage ?? initialValues?.dosage ?? '',
    quantityCustom: medication?.quantityCustom ?? '',
    dosagePending:
      medication?.dosagePending ?? initialValues?.dosagePending ?? false,
    frequencyType: medication?.frequencyType ?? '',
    frequencyValue: medication?.frequencyValue ?? '',
    frequencyCustom: medication?.frequencyCustom ?? '',
    durationType: medication?.durationType ?? '',
    durationValue: medication?.durationValue ?? '',
    durationCustom: medication?.durationCustom ?? '',
    dosageSchedule: medication?.dosageSchedule ?? '',

    indication: medication?.indication ?? '',
    adverseReaction: medication?.adverseReaction ?? false,
    adverseReactionNote: medication?.adverseReactionNote ?? '',
    // A new course with no start date is already treated as started today —
    // `hasStarted()` reads a missing date as "yes", which is what makes the
    // status ACTIVE rather than SCHEDULED. Showing today says so, instead of
    // leaving an empty field the professional has to know the meaning of.
    // Editing keeps whatever was stored, blank included.
    startedAt: medication
      ? medication.startedAt
        ? new Date(medication.startedAt)
        : null
      : new Date(),

    status: medication?.status ?? 'ACTIVE',
    stopReason: medication?.stopReason ?? '',
    stopReasonNote: medication?.stopReasonNote ?? '',
    endedAt: medication?.endedAt ? new Date(medication.endedAt) : null,

    dosage: medication?.dosage ?? initialValues?.dosage ?? 0,
    dosageInterval:
      medication?.dosageInterval ?? initialValues?.dosageInterval ?? 'Day',
    notes: medication?.notes || initialValues?.notes || '',

    reminderEnabled: hideReminder ? false : Boolean(linkedReminder),
    reminderDescription: linkedReminder?.description || '',
    reminderScheduledAt: linkedReminder
      ? new Date(linkedReminder.scheduledAt)
      : new Date(),
    reminderRecurrenceType:
      (linkedReminder?.recurrenceType as CreateReminderDtoRecurrenceTypeEnum) ||
      CreateReminderDtoRecurrenceTypeEnum.Single,
    reminderCustomEvery: linkedReminder?.customRecurrence?.every || 1,
    reminderCustomUnit:
      (linkedReminder?.customRecurrence
        ?.unit as CreateReminderDtoCustomRecurrenceUnitEnum) ||
      CreateReminderDtoCustomRecurrenceUnitEnum.Week,
    reminderCustomDayOfWeek: linkedReminder?.customRecurrence?.dayOfWeek ?? '',
    reminderCustomWeekOfMonth:
      (linkedReminder?.customRecurrence
        ?.weekOfMonth as CreateReminderDtoCustomRecurrenceWeekOfMonthEnum) || '',
    reminderEmailEnabled: linkedReminder
      ? linkedReminder.notificationChannels.includes(
          CreateReminderDtoNotificationChannelsEnum.Email as unknown as (typeof linkedReminder.notificationChannels)[0],
        )
      : true,
    reminderSmsEnabled: linkedReminder
      ? linkedReminder.notificationChannels.includes(
          CreateReminderDtoNotificationChannelsEnum.Sms as unknown as (typeof linkedReminder.notificationChannels)[0],
        )
      : true,
    ...(initialStock ?? buildStockDefaults(medication?.stockIssues)),
  };
}
